/**
 * Adapter PURO — sinais de fila/capacidade da agenda nativa no painel do Supervisor
 * (Frente B — reconciliação da agenda, Sessão 5). Sem I/O, sem React: só transforma o
 * wire das agregações do backend em view-models exibíveis (labels PT, tom do fill-rate,
 * recorte "perto do limite"). A garantia mora no backend; aqui só APRESENTAMOS.
 */
import { tierLabel } from '../../../appointments/waitlist-adapter';

import type { CapacitySignalsWire, WaitlistSignalsWire } from '../../../../lib/api-client';

export type FillRateTone = 'good' | 'warn' | 'bad' | 'none';

export interface WaitlistSignalsVM {
  waiting: number;
  pendingApproval: number;
  /** Fila viva por tier, urgência destacada (já ordenada por rank pelo backend). */
  tiers: { label: string; count: number; urgent: boolean }[];
  /** Top serviços por fila (recorte para o card). */
  topServices: { serviceOfferingId: string; name: string; count: number }[];
  fillRate: {
    windowDays: number;
    booked: number;
    resolved: number;
    ratePct: number | null;
    tone: FillRateTone;
  };
}

export interface CapacityResourceVM {
  resourceId: string;
  name: string;
  typeLabel: string;
  capacity: number;
  peakConcurrent: number;
  utilizationPct: number;
  /** Frente B Plano 3 (C1) — ocupação-de-tempo (exclusivo); `null` p/ grupo. */
  timeUtilizationPct: number | null;
  /**
   * Frente B Plano 3 (C1) — a % HONESTA a exibir: grupo → capacidade (`utilizationPct`);
   * exclusivo → tempo (`timeUtilizationPct`). Casa com o `metricLabel`.
   */
  displayUtilizationPct: number;
  /** Rótulo do que a % mede: "da capacidade" (grupo) vs "da grade" (exclusivo). */
  metricLabel: string;
  nearLimit: boolean;
  /** Recurso de grupo (capacity > 1) — a pressão de capacidade só se aplica a grupos. */
  isGroup: boolean;
}

export interface CapacitySignalsVM {
  horizonDays: number;
  /** Recursos de grupo em pressão de capacidade (nearLimit) — o sinal acionável. */
  nearLimit: CapacityResourceVM[];
  /** Recursos com ocupação no horizonte (contexto), já ordenados por utilização. */
  active: CapacityResourceVM[];
  totalResources: number;
}

const RESOURCE_TYPE_LABELS: Record<CapacitySignalsWire['resources'][number]['resourceType'], string> = {
  professional: 'Profissional',
  chair: 'Cadeira',
  room: 'Sala',
  equipment: 'Equipamento',
  other: 'Recurso',
};

const URGENT_TIERS = new Set(['bumped', 'urgent_high', 'urgent_medium', 'urgent_low']);

/** Tom do fill-rate: >=70% saudável, 40–69% atenção, <40% crítico; null = sem dado. */
export function fillRateTone(ratePct: number | null): FillRateTone {
  if (ratePct === null) return 'none';
  if (ratePct >= 70) return 'good';
  if (ratePct >= 40) return 'warn';
  return 'bad';
}

export function adaptWaitlistSignals(
  wire: WaitlistSignalsWire,
  opts: { topServices?: number } = {},
): WaitlistSignalsVM {
  const topN = opts.topServices ?? 4;
  return {
    waiting: wire.waiting,
    pendingApproval: wire.pendingApproval,
    tiers: wire.byTier.map((t) => ({
      label: tierLabel(t.tier),
      count: t.count,
      urgent: URGENT_TIERS.has(t.tier),
    })),
    topServices: wire.byService.slice(0, topN).map((s) => ({
      serviceOfferingId: s.serviceOfferingId,
      name: s.serviceName,
      count: s.count,
    })),
    fillRate: {
      windowDays: wire.fillRate.windowDays,
      booked: wire.fillRate.booked,
      resolved: wire.fillRate.resolved,
      ratePct: wire.fillRate.ratePct,
      tone: fillRateTone(wire.fillRate.ratePct),
    },
  };
}

export function adaptCapacitySignals(wire: CapacitySignalsWire): CapacitySignalsVM {
  const rows: CapacityResourceVM[] = wire.resources.map((r) => {
    const isGroup = r.capacity > 1;
    return {
      resourceId: r.resourceId,
      name: r.resourceName,
      typeLabel: RESOURCE_TYPE_LABELS[r.resourceType],
      capacity: r.capacity,
      peakConcurrent: r.peakConcurrent,
      utilizationPct: r.utilizationPct,
      timeUtilizationPct: r.timeUtilizationPct,
      displayUtilizationPct: isGroup ? r.utilizationPct : (r.timeUtilizationPct ?? 0),
      metricLabel: isGroup ? 'da capacidade' : 'da grade',
      nearLimit: r.nearLimit,
      isGroup,
    };
  });
  return {
    horizonDays: wire.horizonDays,
    nearLimit: rows.filter((r) => r.nearLimit),
    // Contexto: recursos com alguma ocupação, ordem do backend (mais pressionado primeiro).
    active: rows.filter((r) => r.peakConcurrent > 0),
    totalResources: rows.length,
  };
}
